import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LDClient, LDFlags } from '~/providers';
import {
  agreeToTerms,
  fetchExistingPlans,
  fetchFunnelPacket,
  fetchVnmTerms,
  redirectToDashboard,
  signUpUser,
} from '~/services';
import { FetchFunnelPacketResponse } from '~/services/fetch-funnel-packet';
import { ContractVersion, FetchVnmTermsResponse } from '~/services/fetch-vnm-terms';
import { ContractTag, OriginationSource } from '~/typings/graphql';
import { INTERNAL_ROUTES, SPONSORED_SOLAR_ROUTES } from '~/utils/constants';
import { fetchContractLanguage } from '~/utils/fetch-contract-language';
import { store } from '~/utils/store';
import { sendToSegment } from '~/utils/tracking';
import { ReviewAndViewTerms } from './components/review-and-view-terms.component';

export interface ReviewAndViewTermsProps {
  userId?: number;
  utilityAccountId?: number;
  contracts: ContractData;
  flags: { showPersonalizedContractPreview: boolean };
  funnelPacket: FetchFunnelPacketResponse;
  error: Error | null;
  loading: boolean;
  onSubmitSolarTerms: () => void;
  pending: boolean;
  originationSourceType?: string;
}

export interface UpdateUserInput {
  id: number | undefined;
  input: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null | undefined;
  };
}

type ContractData = {
  docuSignContracts: FetchVnmTermsResponse['docuSignContracts'];
  showLmi?: boolean;
  vnmSolarProjectContracts: ContractVersion[];
};

export const ReviewAndViewTermsWrapper = () => {
  const userId = Number(store.get('USER_ID')!);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [funnelPacket, setFunnelPacket] = useState<FetchFunnelPacketResponse>();
  const [contracts, setContracts] = useState<ContractData>();
  const [termsCompleted, setTermsCompleted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    }
  }, [navigate, userId]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setError(null);
        setLoading(true);
        const funnelPacket = await fetchFunnelPacket({ userId });
        setFunnelPacket(funnelPacket);
        sendToSegment({
          type: 'legal_step_started',
          funnelPacket,
        });
      } catch (e) {
        setError(new Error('Something went wrong. Please try again.'));
      }
      setLoading(false);
    };
    fetchUserDetails();
  }, [userId]);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setError(null);
        setLoading(true);
        const contractTags =
          funnelPacket?.user?.originationSource?.originationSourceType ===
          OriginationSource.SponsoredSolar
            ? [ContractTag.SponsoredSolar]
            : undefined;
        const contracts = await fetchVnmTerms({
          stateId: funnelPacket?.utility?.state.id!,
          utilityAccountId: funnelPacket?.utilityAccount?.id!,
          contractTags,
        });
        const {
          docuSignContracts,
          showLmi: showLmiFromTerms,
          generalContracts,
          vnmSolarProjectContracts,
        } = contracts;

        const allSolarContracts = generalContracts
          ? vnmSolarProjectContracts?.concat(generalContracts)
          : vnmSolarProjectContracts;

        const unsignedSolarContracts = allSolarContracts?.filter(
          contractVersion => !funnelPacket?.signedContractIds.includes(contractVersion.contractId),
        );

        const data = await fetchContractLanguage(
          docuSignContracts,
          unsignedSolarContracts,
          showLmiFromTerms,
        );

        setContracts({
          docuSignContracts: data?.docuSignContracts,
          showLmi: data?.showLmi,
          vnmSolarProjectContracts: data?.vnmSolarProjectContracts,
        });
        setLoading(false);
      } catch (e) {
        setLoading(false);
        setError(new Error('Something went wrong. Please try again.'));
      }
    };
    if (funnelPacket) {
      fetchTerms();
    }
  }, [funnelPacket]);

  useEffect(() => {
    const signUpCompleted = async () => {
      try {
        setError(null);
        sendToSegment({
          type: 'funnel_completed',
          funnelPacket: funnelPacket!,
          track: funnelPacket?.utility?.funnelTrack,
        });
        const { plan } = await signUpUser({
          utilityAccountId: funnelPacket?.utilityAccount?.id!,
          planId: funnelPacket?.customPlan?.id!,
          enrollmentDataSourceName:
            store.get('ENROLLMENT_DATA_SOURCE_NAME')! ||
            funnelPacket?.utilityAccount.enrollmentDataSourceName ||
            'none',
          billingExperienceType: 'none',
          track: funnelPacket?.utility.funnelTrack!,
          source: 'sponsored_solar',
        });

        const response = await fetchExistingPlans({ userId });
        const plans = response?.existingPlans.filter(customPlan => customPlan.active === true);
        const planId = funnelPacket?.customPlan?.id! || plan.id || plans?.[0].id!;
        let path = `/${SPONSORED_SOLAR_ROUTES.DASHBOARD_ROUTE}`;
        path = path.replace('planId', planId.toString());
        const params = {
          userId: funnelPacket?.user?.id! || userId,
          utilityAccountId: funnelPacket?.utilityAccount?.id!,
          billingExperienceType: 'none',
          ...(path ? { path } : {}),
        };
        redirectToDashboard(params);
      } catch (e) {
        setPending(false);
        setError(new Error('Something went wrong. Please try again.'));
      }
    };
    if (termsCompleted) {
      signUpCompleted();
    }
  }, [termsCompleted]);

  const onSubmitSolarTerms = useCallback(async () => {
    try {
      setError(null);
      setPending(true);
      setTermsCompleted(false);
      await agreeToTerms({
        userId: funnelPacket?.user?.id!,
        utilityAccountId: funnelPacket?.utilityAccount?.id!,
        vnmSolarProjectContracts: contracts?.vnmSolarProjectContracts,
      });
      setTermsCompleted(true);
      sendToSegment({
        type: 'legal_step_completed',
        contractIds: contracts?.vnmSolarProjectContracts?.map(c => c.id) || [],
        funnelPacket: funnelPacket!,
      });
    } catch (e) {
      setPending(false);
      setError(new Error('Something went wrong. Please try again.'));
    }
  }, [contracts, funnelPacket]);

  const reviewAndViewTermsProps: ReviewAndViewTermsProps = {
    userId,
    utilityAccountId: funnelPacket?.utilityAccount?.id!,
    contracts: contracts!,
    flags: {
      showPersonalizedContractPreview: LDClient.variation(LDFlags.showPersonalizedContractPreview),
    },
    funnelPacket: funnelPacket!,
    error,
    loading,
    onSubmitSolarTerms,
    pending,
    originationSourceType: funnelPacket?.user.originationSource?.originationSourceType,
  };

  return <ReviewAndViewTerms {...reviewAndViewTermsProps} />;
};
